import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Link from '@material-ui/core/Link';
import Avatar from '@material-ui/core/Avatar';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Container from 'common/Container';
import stripTrailingPeriod from 'utils/stripTrailingPeriod';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import GavelIcon from '@material-ui/icons/Gavel';
import BuildIcon from '@material-ui/icons/Build';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import SecurityIcon from '@material-ui/icons/Security';
import PeopleIcon from '@material-ui/icons/People';
import DescriptionIcon from '@material-ui/icons/Description';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import StarIcon from '@material-ui/icons/Star';
import Slider from 'react-slick';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import ReplayIcon from '@mui/icons-material/Replay';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';

const BENEFIT_ICON_MAP = {
  Gavel: GavelIcon,
  Build: BuildIcon,
  AccountBalance: AccountBalanceIcon,
  Security: SecurityIcon,
  People: PeopleIcon,
  Description: DescriptionIcon,
  TrendingUp: TrendingUpIcon,
};

const TRANSMISSION_ICON_MAP = {
  live: LiveTvIcon,
  replay: ReplayIcon,
  ondemand: OndemandVideoIcon,
};

const METRIC_ICON_MAP = {
  instagram: InstagramIcon,
  web: LanguageIcon,
  audience: PeopleIcon,
  live: LiveTvIcon,
  ondemand: OndemandVideoIcon,
};

const ProgramProposalLayout = ({ content, themeMode, proposalName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), {
    defaultMatches: true,
  });
  const who = content.who || {};
  const videoUrl = content.videoUrl || '';
  const benefitsSection = content.benefitsSection || {};
  const benefitsRaw = Array.isArray(content.benefits) ? content.benefits : [];
  const benefits = benefitsRaw.map((b) => (typeof b === 'string' ? { title: '', text: b, icon: 'Gavel' } : { title: b.title || '', text: b.text || b.subtitle || '', icon: b.icon || 'Gavel' })).filter((b) => b.title || b.text);
  const metrics = Array.isArray(content.metrics) ? content.metrics : [];
  const metricsSection = content.metricsSection || {};
  const transmissionFlow = content.transmissionFlow || {};
  const transmissionSteps = Array.isArray(transmissionFlow.steps) ? transmissionFlow.steps : [];
  const values = content.values || {};
  const valueItemsRaw = Array.isArray(values.items) ? values.items : [];
  const valueItems = valueItemsRaw.map((item) => {
    if (typeof item === 'string') {
      const separatorIndex = item.indexOf(':');
      if (separatorIndex >= 0) {
        return {
          title: item.slice(0, separatorIndex).trim(),
          text: item.slice(separatorIndex + 1).trim(),
        };
      }
      return { title: '', text: item };
    }
    return {
      title: item.title || '',
      text: item.text || item.subtitle || '',
    };
  }).filter((item) => item.title || item.text);
  const structureTable = content.structureTable || {};
  const structureRows = Array.isArray(structureTable.rows) ? structureTable.rows : [];
  const pricingOptions = Array.isArray(content.pricingOptions) ? content.pricingOptions : [];
  const pricingSection = content.pricingSection || {};
  const areYouReady = content.areYouReady || {};
  const sliderOpts = {
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isMobile,
    prevArrow: !isMobile && (
      <ArrowCircleLeftIcon
        sx={{
          fontSize: 40,
          color: theme.palette.primary.main,
          '&:hover': {
            color: theme.palette.secondary.main,
          },
        }}
      />
    ),
    nextArrow: !isMobile && (
      <ArrowCircleRightIcon
        sx={{
          fontSize: 40,
          color: theme.palette.primary.main,
          '&:hover': {
            color: theme.palette.secondary.main,
          },
        }}
      />
    ),
  };

  return (
    <>
      <Container>
        <Box marginBottom={4}>
          <Container
            sx={{
              backgroundImage: themeMode === 'dark' ? 'url(/images/logos/logo_white.PNG)' : 'url(/images/logos/logo_tnonetwork_black.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              height: '20vh',
            }}
          />
          {who.title && (
            <Box component={Typography} fontWeight={700} variant="h3" gutterBottom align="center" color={theme.palette.primary.main}>
              {stripTrailingPeriod(who.title)}
            </Box>
          )}
          {who.description && (
            <Typography variant="h6" component="p" color="textSecondary" align="center">
              {who.description}
            </Typography>
          )}
          {who.sectionTitle && (
            <Typography variant="h5" component="p" color="primary" align="center" sx={{ mt: 3, mb: 2, fontStyle: 'italic' }}>
              {who.sectionTitle}
            </Typography>
          )}
          {who.sectionLinkUrl && (
            <Typography align="center" sx={{ mb: 1 }}>
              <Link
                href={who.sectionLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
              >
                {who.sectionLinkLabel || who.sectionLinkUrl}
              </Link>
            </Typography>
          )}
          {who.logoUrl && (
            <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 3 }}>
              <Box
                component="img"
                src={who.logoUrl}
                alt={proposalName ? `Logo de ${proposalName}` : 'Logo del programa'}
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
                sx={{
                  width: '100%',
                  maxWidth: 320,
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
          {who.sectionBody && (
            <Typography variant="body1" component="p" color="textSecondary" align="center" sx={{ mt: 1 }}>
              {who.sectionBody}
            </Typography>
          )}
        </Box>
      </Container>

      {(who.anchorName || who.anchorImageUrl) && (
        <Container>
          <Box marginTop={6} marginBottom={4}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box display="flex" justifyContent="center">
                  <Box
                    component="img"
                    src={who.anchorImageUrl}
                    alt={who.anchorName ? `Nuestra ancla, ${who.anchorName}` : 'Nuestra ancla'}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                    sx={{
                      width: { xs: 200, sm: 250, md: 300 },
                      height: { xs: 200, sm: 250, md: 300 },
                      borderRadius: 2,
                      objectFit: 'cover',
                      boxShadow: 3,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
                  NUESTRA ANCLA{who.anchorName ? `: ${who.anchorName.toUpperCase()}` : ''}
                </Typography>
                {who.anchorSubtitle && (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                    {who.anchorSubtitle}
                  </Typography>
                )}
                {who.anchorBio && (
                  <Typography variant="body1" color="textSecondary">
                    {who.anchorBio}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Container>
      )}

      {videoUrl && (
        <Container>
          <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 4, boxShadow: 3 }}>
            <Box
              component="iframe"
              src={videoUrl}
              sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={proposalName}
            />
          </Box>
        </Container>
      )}

      {(benefitsSection.title || benefitsSection.description || benefits.length > 0) && (
        <Container>
          <Box marginBottom={4}>
            {benefitsSection.title && (
              <Typography variant="h5" color="primary" align="center" gutterBottom>
                {stripTrailingPeriod(benefitsSection.title)}
              </Typography>
            )}
            {benefitsSection.description && (
              <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 720, mx: 'auto', mb: 3 }}>
                {benefitsSection.description}
              </Typography>
            )}
            <Grid container spacing={2}>
              {benefits.map((b, i) => {
                const IconComponent = BENEFIT_ICON_MAP[b.icon] || StarIcon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        {b.title && <Typography fontWeight={600} gutterBottom>{b.title}</Typography>}
                        {b.icon && (
                          <Box sx={{ mb: 1 }}>
                            <IconComponent color="primary" sx={{ fontSize: 28 }} />
                          </Box>
                        )}
                        {b.text && <Typography variant="body2" color="textSecondary">{b.text}</Typography>}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Container>
      )}

      {metrics.length > 0 && (
        <Container>
          <Box marginBottom={4}>
            <Typography
              sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}
              gutterBottom
              color="primary"
              align="center"
            >
              {metricsSection.overline || 'Métricas'}
            </Typography>
            <Typography variant="h5" color="primary" align="center" gutterBottom>
              {stripTrailingPeriod(metricsSection.title || 'Métricas')}
            </Typography>
            {metricsSection.description && (
              <Typography
                variant="body1"
                color="textSecondary"
                align="center"
                sx={{ maxWidth: 760, mx: 'auto', mb: 3 }}
              >
                {metricsSection.description}
              </Typography>
            )}
            <Grid container spacing={2}>
              {metrics.map((m, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: theme.palette.primary.main + '14', borderRadius: 2 }}>
                    {m.icon && METRIC_ICON_MAP[m.icon] && (
                      <Box sx={{ mb: 1, color: theme.palette.primary.main }}>
                        {React.createElement(METRIC_ICON_MAP[m.icon], { sx: { fontSize: 32 } })}
                      </Box>
                    )}
                    <Typography variant="h4" color="primary">{m.value != null ? m.value : m.title}</Typography>
                    {m.label && <Typography variant="body2" color="textSecondary">{m.label}</Typography>}
                    {m.subtitle && <Typography variant="caption" color="textSecondary">{m.subtitle}</Typography>}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      )}

      {(transmissionFlow.title || transmissionSteps.length > 0) && (
        <Container>
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography
              sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}
              gutterBottom
              color="primary"
              align="center"
            >
              {transmissionFlow.overline || 'Ciclo de transmisión'}
            </Typography>
            <Typography
              component="h3"
              variant="h5"
              fontWeight={700}
              align="center"
              color="primary"
              gutterBottom
            >
              {stripTrailingPeriod(transmissionFlow.title)}
            </Typography>
            {transmissionFlow.description && (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                {transmissionFlow.description}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: isMobile ? 2 : 0,
              }}
            >
              {transmissionSteps.map((step, index) => {
                const IconComponent = TRANSMISSION_ICON_MAP[step.icon] || LiveTvIcon;
                const isLast = index === transmissionSteps.length - 1;
                return (
                  <React.Fragment key={`${step.label}-${index}`}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: isMobile ? '100%' : 100,
                        maxWidth: isMobile ? 280 : 120,
                        px: 1.5,
                        py: 2,
                        borderRadius: 2,
                        border: `2px solid ${theme.palette.primary.main}`,
                        bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'background.paper',
                        boxShadow: 1,
                      }}
                    >
                      <Box sx={{ color: theme.palette.primary.main, mb: 0.5 }}>
                        <IconComponent sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} align="center">
                        {step.label}
                      </Typography>
                      {step.sublabel && (
                        <Typography variant="caption" color="textSecondary" align="center">
                          {step.sublabel}
                        </Typography>
                      )}
                    </Box>
                    {!isLast && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mx: isMobile ? 0 : 0.5,
                          transform: isMobile ? 'rotate(90deg)' : 'none',
                        }}
                      >
                        <ArrowForwardIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                            opacity: 0.8,
                          }}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Container>
      )}

      {(values.title || valueItems.length > 0) && (
        <Container>
          <Box marginBottom={4}>
            {values.title && (
              <Typography variant="h5" color="primary" align="center" gutterBottom>
                {stripTrailingPeriod(values.title)}
              </Typography>
            )}
            <Grid container spacing={3}>
              {valueItems.map((item, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        p: 4,
                        '&:last-child': { pb: 4 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          bgcolor: theme.palette.primary.main + '14',
                          color: theme.palette.primary.main,
                        }}
                      >
                        <StarIcon sx={{ fontSize: 32 }} />
                      </Box>
                      {item.title && (
                        <Typography variant="h6" gutterBottom fontWeight={600}>
                          {item.title}
                        </Typography>
                      )}
                      {item.text && (
                        <Typography variant="body2" color="textSecondary">
                          {item.text}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      )}

      {structureTable.title && structureRows.length > 0 && (
        <Container>
          <Box marginBottom={4}>
            <Typography variant="h5" color="primary" align="center" gutterBottom>{stripTrailingPeriod(structureTable.title)}</Typography>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Categoría</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Pilar de contenido</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Objetivo de marca</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {structureRows.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>{row.pillar}</TableCell>
                      <TableCell>{row.objective}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Container>
      )}

      {(pricingOptions.length > 0 || pricingSection.title || pricingSection.description) && (
        <Container>
          <Box marginBottom={4}>
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
              {pricingSection.overline || 'Opciones de Comercialización'}
            </Typography>
            <Box component={Typography} fontWeight={700} variant="h5" gutterBottom align="center" color="primary">
              {stripTrailingPeriod(pricingSection.title || proposalName)}
            </Box>
            {pricingSection.description && (
              <Typography
                variant="body1"
                color="textSecondary"
                align="center"
                sx={{ maxWidth: 760, mx: 'auto' }}
              >
                {pricingSection.description}
              </Typography>
            )}
            {pricingOptions.length > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginTop={2}
                gap={1}
              >
                <ArrowForwardIcon
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: 20,
                    animation: 'swipe 2s ease-in-out infinite',
                    '@keyframes swipe': {
                      '0%, 100%': { transform: 'translateX(0)' },
                      '50%': { transform: 'translateX(10px)' },
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  Desliza para ver más opciones
                </Typography>
                <ArrowForwardIcon
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: 20,
                    animation: 'swipe 2s ease-in-out infinite',
                    '@keyframes swipe': {
                      '0%, 100%': { transform: 'translateX(0)' },
                      '50%': { transform: 'translateX(10px)' },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
          {pricingOptions.length > 0 && (
            <Slider {...sliderOpts}>
              {pricingOptions.map((opt, i) => (
                <Box key={i} padding={{ xs: 1, sm: 3, md: 4 }}>
                  <Grid container spacing={isMobile ? 2 : 0} sx={{ maxWidth: 1100, mx: 'auto' }}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <Box component={CardContent} padding={{ sm: 2 }}>
                          {(opt.priceLabel || opt.monthlyPrice != null) && (
                            <Box marginBottom={2}>
                              <Typography
                                fontWeight={600}
                                variant="h4"
                                align="center"
                                gutterBottom
                              >
                                {opt.priceLabel || `$${opt.monthlyPrice} ${opt.duration || '/ mes'}`}
                              </Typography>
                            </Box>
                          )}
                          <Box marginBottom={2}>
                            <Typography
                              variant="h4"
                              color={theme.palette.primary.main}
                              fontWeight={600}
                              align="center"
                              gutterBottom
                            >
                              {opt.title}
                            </Typography>
                            <Typography
                              color="textSecondary"
                              align="left"
                              marginLeft={2}
                              marginTop="2rem"
                            >
                              EN QUÉ CONSISTE
                            </Typography>
                          </Box>
                          <Grid container spacing={1}>
                            {(Array.isArray(opt.features) ? opt.features : []).map((item, j) => (
                              <Grid item xs={12} sm={6} key={j}>
                                <Box component={ListItem} disableGutters width="auto" padding={0}>
                                  <Box component={ListItemAvatar} minWidth="auto !important" marginRight={2}>
                                    <Box
                                      component={Avatar}
                                      bgcolor={theme.palette.secondary.main}
                                      width={20}
                                      height={20}
                                    >
                                      <svg
                                        width={12}
                                        height={12}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </Box>
                                  </Box>
                                  <ListItemText primary={item} />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            marginTop="1rem"
                            paddingX={2}
                            flexDirection={isMobile ? 'column' : 'row'}
                          >
                            <Box flex="1">
                              <Typography color="textSecondary" align="left" marginTop="2rem">
                                IDEAL PARA
                              </Typography>
                              <Typography color="textSecondary" align="left" marginTop="1rem">
                                {opt.idealFor}
                              </Typography>
                            </Box>
                            <Box flex="1">
                              <Typography
                                color="textSecondary"
                                align={isMobile ? 'left' : 'right'}
                                marginTop="2rem"
                              >
                                DURACIÓN MÍNIMA
                              </Typography>
                              <Typography
                                color="textSecondary"
                                align={isMobile ? 'left' : 'right'}
                                marginTop="1rem"
                              >
                                {opt.duration || 'Mensual'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Divider />
                        <Box component={CardActions} justifyContent="center">
                          <Button
                            component="a"
                            href="https://wa.me/584241234567"
                            target="_blank"
                            rel="noopener noreferrer"
                            size="large"
                          >
                            {opt.buttonText}
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                    <Grid item container xs={12} md={6} alignItems="center">
                      <Box
                        component={Card}
                        bgcolor={theme.palette.primary.main}
                        sx={{ width: '100%' }}
                      >
                        <Box
                          component={CardContent}
                          padding={{ sm: 4 }}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Box color={theme.palette.common.white} marginBottom={4}>
                            <StarIcon sx={{ fontSize: '4rem' }} />
                          </Box>
                          <Box
                            component={Typography}
                            variant="h5"
                            color={theme.palette.common.white}
                            fontWeight={600}
                            align="center"
                          >
                            {opt.highlightedTitle || 'Ideal para'}
                          </Box>
                          {(Array.isArray(opt.highlightedContent) && opt.highlightedContent.length > 0
                            ? opt.highlightedContent
                            : [opt.idealFor]
                          ).map((content, index) => (
                            <Box
                              key={index}
                              component={Typography}
                              color={theme.palette.common.white}
                              align="center"
                              sx={{ mt: 1 }}
                            >
                              {content}
                            </Box>
                          ))}
                        </Box>
                        <Divider />
                        <Box component={CardActions} justifyContent="center">
                          <Button
                            component="a"
                            href="https://wa.me/584241234567"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: theme.palette.common.white }}
                            size="large"
                          >
                            {opt.highlightedButtonText || opt.buttonText}
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Slider>
          )}
        </Container>
      )}

      {(areYouReady.title || areYouReady.description || areYouReady.footerText) && (
        <Container>
          <Box marginBottom={4} textAlign="center">
            {areYouReady.title && <Typography variant="h4" color="primary" gutterBottom>{stripTrailingPeriod(areYouReady.title)}</Typography>}
            {areYouReady.description && <Typography variant="body1" color="textSecondary" paragraph>{areYouReady.description}</Typography>}
            {areYouReady.ctaText && (
              <Button component="a" href="https://wa.me/584241234567" target="_blank" rel="noopener noreferrer" color="primary" variant="contained" size="large" sx={{ mt: 2 }}>
                {areYouReady.ctaText}
              </Button>
            )}
            {areYouReady.contactEmail && (
              <Box sx={{ mt: 2 }}>
                <Button
                  component="a"
                  href={`mailto:${areYouReady.contactEmail}`}
                  color="primary"
                  variant="outlined"
                  size="large"
                >
                  {areYouReady.contactEmail}
                </Button>
              </Box>
            )}
            {areYouReady.footerText && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>{areYouReady.footerText}</Typography>
            )}
            {areYouReady.footerSecondaryText && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {areYouReady.footerSecondaryText}
              </Typography>
            )}
          </Box>
        </Container>
      )}
    </>
  );
};

ProgramProposalLayout.propTypes = {
  content: PropTypes.object.isRequired,
  themeMode: PropTypes.string,
  proposalName: PropTypes.string,
};

export default ProgramProposalLayout;
